import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { PaymentInterface } from '@contracts/interfaces/invoice.interface';
import { InvoicesService } from 'src/app/services/invoices.service';
import { TransactionsService } from 'src/app/services/transactions.service';
import { Transaction } from '@contracts/interfaces/transaction.interfaces';

export interface PaymentDetailData {
  invoiceNumber: number;
  invoiceType?: string;
  invoiceStatus?: string;
  paymentId?: string;
  customer?: any;
  payment: PaymentInterface;
  total?: number;
  amountDue?: number;
  dateCreated?: Date | string;
  transactionSha1: string | null;
}

@Component({
  selector: 'app-payment-detail-dialog',
  templateUrl: './payment-detail.dialog.html',
  styleUrls: ['./payment-detail.dialog.scss']
})
export class PaymentDetailDialogComponent implements OnInit {
  constructor(
    private dialogRef: MatDialogRef<PaymentDetailDialogComponent>,
    private invoicesService: InvoicesService,
    private transactionsService: TransactionsService,
    @Inject(MAT_DIALOG_DATA) public data: PaymentDetailData,
  ) {}

  transactionSha1Input: string = '';
  linkError: string | null = null;
  isLinking = false;
  isLoadingSuggestions = false;
  suggestionLabel = 'Suggested transactions';
  hasSearchedSuggestions = false;
  suggested: Transaction[] = [];

  ngOnInit(): void {
    this.transactionSha1Input = this.data.transactionSha1 || '';
    this.loadSuggestions();
  }

  get customerName(): string {
    const customer = this.data?.customer;
    if (!customer) return '';
    if (customer.name === '' && customer.business) {
      return customer.business;
    }
    return customer.name || customer.business || '';
  }

  linkTransaction() {
    this.linkError = null;
    const sha = (this.transactionSha1Input || '').trim();
    if (!sha) {
      this.linkError = 'Enter a transaction hash to link.';
      return;
    }
    this.isLinking = true;

    // Validate transaction existence before linking.
    this.transactionsService.get(sha).subscribe({
      next: () => {
        this.invoicesService.linkPaymentToTransaction(
          this.data.invoiceNumber,
          this.data.payment?.paymentId || this.data.paymentId,
          sha
        ).subscribe({
          next: (resp: any) => {
            this.isLinking = false;
            this.data.transactionSha1 = sha;
            this.dialogRef.close({ linked: true, payment: resp?.payment });
          },
          error: (err) => {
            this.isLinking = false;
            this.linkError = err?.error?.error || 'Failed to link payment.';
          }
        });
      },
      error: () => {
        this.isLinking = false;
        this.linkError = 'Transaction not found. Check the hash and try again.';
      }
    });
  }

  loadSuggestions() {
    this.isLoadingSuggestions = true;
    this.suggestionLabel = 'Suggested transactions';
    this.hasSearchedSuggestions = true;
    this.suggested = [];
    const invoiceNumber = this.data.invoiceNumber;
    const paymentAmount = this.data.payment?.amount;

    const baseQuery: any = {
      description: { $regex: `${invoiceNumber}`, $options: 'i' }
    };

    // Try to narrow by amount if available.
    if (paymentAmount != null) {
      baseQuery.amount = paymentAmount;
    }

    this.transactionsService.find(baseQuery, 0, 6, { date: -1 }).subscribe({
      next: (transactions: Transaction[]) => {
        this.suggested = transactions || [];
        this.isLoadingSuggestions = false;
      },
      error: () => {
        // Fallback: try only amount if description search fails.
        if (paymentAmount != null) {
          this.transactionsService.find({ amount: paymentAmount }, 0, 6, { date: -1 }).subscribe({
            next: (transactions: Transaction[]) => {
              this.suggested = transactions || [];
              this.isLoadingSuggestions = false;
            },
            error: () => {
              this.isLoadingSuggestions = false;
            }
          });
        } else {
          this.isLoadingSuggestions = false;
        }
      }
    });
  }

  searchByAmount() {
    const paymentAmount = this.data.payment?.amount;
    if (paymentAmount == null || this.isLoadingSuggestions) {
      return;
    }

    this.isLoadingSuggestions = true;
    this.suggestionLabel = `Transactions matching ${this.formatAmount(paymentAmount)}`;
    this.hasSearchedSuggestions = true;
    this.suggested = [];

    this.transactionsService.find({
      amount: {
        $in: [paymentAmount, -paymentAmount]
      }
    }, 0, 50, { date: -1 }).subscribe({
      next: (transactions: Transaction[]) => {
        this.suggested = transactions || [];
        this.isLoadingSuggestions = false;
      },
      error: () => {
        this.isLoadingSuggestions = false;
      }
    });
  }

  useSuggestion(tx: Transaction) {
    if (!tx?.source?.sha1) return;
    this.transactionSha1Input = tx.source.sha1;
    this.linkTransaction();
  }

  private formatAmount(amount: number) {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(amount / 100);
  }
}
